(() => {
  const STICKY_ROOT_SELECTOR = '[data-sticky-pdp-controls], .sticky-pdp-controls';
  const ORIGINAL_FORM_SELECTOR = 'form#new_pdp_form';

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  };

  const dispatchChange = (element) => {
    if (!element) return;
    element.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const getControlsByName = (form, name) => {
    if (!form || !name) return [];
    return Array.from(form.elements).filter((element) => element.name === name);
  };

  const findInputByIdInside = (container, id) => {
    if (!container || !id) return null;

    return Array.from(container.querySelectorAll('[id]')).find((element) => {
      return element.id === id;
    });
  };

  const getOriginalForm = (stickyRoot) => {
    const forms = Array.from(document.querySelectorAll(ORIGINAL_FORM_SELECTOR));

    return forms.find((form) => {
      return !stickyRoot.contains(form);
    }) || null;
  };

  const getStickyForm = (stickyRoot) => {
    return stickyRoot.querySelector('form');
  };

  const getOptionName = (control) => {
    return control.closest('.option')?.dataset?.name || '';
  };

  const findMatchingOriginalOption = (originalForm, optionName) => {
    if (!originalForm || !optionName) return null;

    return Array.from(originalForm.querySelectorAll('.option')).find((option) => {
      return option.dataset.name === optionName;
    });
  };

  const findControlByValue = (container, selector, value) => {
    if (!container) return null;

    return Array.from(container.querySelectorAll(selector)).find((control) => {
      return control.value === value;
    });
  };

  const syncStickyOptionToOriginal = (stickyControl, stickyForm, originalForm) => {
    if (!stickyControl || !stickyForm || !originalForm) return;

    const optionName = getOptionName(stickyControl);
    const originalOption = findMatchingOriginalOption(originalForm, optionName);

    if (!originalOption) return;

    if (stickyControl.matches('input[type="radio"]')) {
      const originalRadio = findControlByValue(
        originalOption,
        'input[type="radio"]',
        stickyControl.value
      );

      if (!originalRadio) return;

      originalRadio.checked = true;
      dispatchChange(originalRadio);
      return;
    }

    if (stickyControl.matches('select')) {
      const originalSelect = originalOption.querySelector('select');

      if (!originalSelect) return;

      originalSelect.value = stickyControl.value;
      dispatchChange(originalSelect);
    }
  };

  const mirrorSelectOptionStates = (sourceSelect, targetSelect) => {
    if (!sourceSelect || !targetSelect) return;

    Array.from(targetSelect.options).forEach((targetOption) => {
      const sourceOption = Array.from(sourceSelect.options).find((option) => {
        return option.value === targetOption.value;
      });

      if (!sourceOption) return;

      targetOption.disabled = sourceOption.disabled;
      targetOption.hidden = sourceOption.hidden;
      targetOption.textContent = sourceOption.textContent;

      if (sourceOption.dataset.leadTime !== undefined) {
        targetOption.dataset.leadTime = sourceOption.dataset.leadTime;
      }

      if (sourceOption.dataset.inventoryMessage !== undefined) {
        targetOption.dataset.inventoryMessage = sourceOption.dataset.inventoryMessage;
      }

      if (sourceOption.dataset.inventoryQuantity !== undefined) {
        targetOption.dataset.inventoryQuantity = sourceOption.dataset.inventoryQuantity;
      }

      if (sourceOption.dataset.noAvailable !== undefined) {
        targetOption.dataset.noAvailable = sourceOption.dataset.noAvailable;
      }
    });
  };

  const isInsideSticky = (element) => {
    return !!element?.closest('[data-sticky-pdp-controls], .sticky-pdp-controls, .sticky-atc-v2');
  };

  const getOriginalLeadTimeElements = () => {
    const originalLtCon = Array.from(document.querySelectorAll('#lt_con')).find((element) => {
      return !isInsideSticky(element);
    });

    const originalShipTime = Array.from(document.querySelectorAll('#ship_time')).find((element) => {
      return !isInsideSticky(element);
    });

    return {
      ltCon: originalLtCon,
      shipTime: originalShipTime
    };
  };

  const getStickyLeadTimeElements = (stickyRoot) => {
    if (!stickyRoot) {
      return {
        ltCon: null,
        shipTime: null
      };
    }

    return {
      ltCon: Array.from(stickyRoot.querySelectorAll('#lt_con')).find(Boolean),
      shipTime: Array.from(stickyRoot.querySelectorAll('#ship_time')).find(Boolean)
    };
  };

  const syncStickyLeadTime = (stickyRoot) => {
    const original = getOriginalLeadTimeElements();
    const sticky = getStickyLeadTimeElements(stickyRoot);

    if (!original.ltCon || !original.shipTime || !sticky.ltCon || !sticky.shipTime) return;

    sticky.shipTime.textContent = original.shipTime.textContent;
    sticky.ltCon.style.display = original.ltCon.style.display;

    if (original.ltCon.className) {
      sticky.ltCon.className = original.ltCon.className;
    }
  };

  const syncOriginalOptionsToSticky = (stickyForm, originalForm) => {
    if (!stickyForm || !originalForm) return;

    originalForm.querySelectorAll('.option:not(.opt-extra)').forEach((originalOption) => {
      const optionName = originalOption.dataset.name;
      if (!optionName) return;

      const stickyOption = Array.from(stickyForm.querySelectorAll('.option:not(.opt-extra)')).find((option) => {
        return option.dataset.name === optionName;
      });

      if (!stickyOption) return;

      const originalRadio = originalOption.querySelector('input[type="radio"]:checked');
      const originalSelect = originalOption.querySelector('select');

      if (originalRadio) {
        const stickyRadio = findControlByValue(
          stickyOption,
          'input[type="radio"]',
          originalRadio.value
        );

        if (stickyRadio) {
          stickyRadio.checked = true;
        }
      }

      if (originalSelect) {
        const stickySelect = stickyOption.querySelector('select');

        if (stickySelect) {
          mirrorSelectOptionStates(originalSelect, stickySelect);
          stickySelect.value = originalSelect.value;
        }
      }
    });

    const originalVariantSelect = originalForm.querySelector('#variant_id, select[name="id"]');
    const stickyVariantSelect = stickyForm.querySelector('#variant_id, select[name="id"]');

    if (originalVariantSelect && stickyVariantSelect) {
      stickyVariantSelect.value = originalVariantSelect.value;
    }

    updateStickyDisplayedSelects(stickyForm);
    syncStickySubmitButton(stickyForm, originalForm);
    syncStickyLeadTime(stickyForm.closest('[data-sticky-pdp-controls], .sticky-pdp-controls, .sticky-atc-v2'));
  };

  const syncStickyExtrasToOriginal = (stickyForm, originalForm) => {
    if (!stickyForm || !originalForm) return;

    const extraControls = stickyForm.querySelectorAll(`
      .opt-extra input,
      .opt-extra select,
      .opt-extra textarea,
      .npdp-engraving input,
      .npdp-engraving select,
      .npdp-engraving textarea,
      .final-sale input,
      .jewerly-inputs input,
      .engraving-inputs input
    `);

    extraControls.forEach((stickyControl) => {
      if (!stickyControl.name) return;
      if (stickyControl.closest('.jewerly-inputs') && stickyControl.disabled) return;
      if (stickyControl.closest('.engraving-inputs') && stickyControl.disabled) return;

      const originalControls = getControlsByName(originalForm, stickyControl.name);
      const originalControl = originalControls[0];

      if (!originalControl) return;

      if (stickyControl.matches('input[type="checkbox"]')) {
        if (originalControl.checked !== stickyControl.checked) {
          originalControl.checked = stickyControl.checked;
          dispatchChange(originalControl);
        }
        return;
      }

      if (stickyControl.matches('input[type="radio"]')) {
        const matchingOriginalRadio = originalControls.find((control) => {
          return control.value === stickyControl.value;
        });

        if (matchingOriginalRadio) {
          matchingOriginalRadio.checked = stickyControl.checked;
          dispatchChange(matchingOriginalRadio);
        }

        return;
      }

      originalControl.value = stickyControl.value;
      dispatchChange(originalControl);
    });
  };

  const updateStickyDisplayedSelects = (stickyForm) => {
    if (!stickyForm) return;

    stickyForm.querySelectorAll('#custom-size-dropdown, .custom-size-dropdown, [data-custom-size-dropdown]').forEach((dropdown) => {
      const select = dropdown.querySelector('select');
      const label = dropdown.querySelector('#current-size, .current-size');

      if (!select || !label) return;

      const selectedOption = select.selectedOptions[0];

      if (!selectedOption) return;

      label.textContent =
        selectedOption.dataset.baseLabel ||
        selectedOption.textContent.replace(/\s+-\s+.+$/, '').trim() ||
        selectedOption.value;
    });
  };

  const syncStickySubmitButton = (stickyForm, originalForm) => {
    const originalSubmit = originalForm.querySelector('input[type="submit"], button[type="submit"]');
    const stickySubmit = stickyForm.querySelector('input[type="submit"], button[type="submit"]');

    if (!originalSubmit || !stickySubmit) return;

    if (originalSubmit.matches('input')) {
      stickySubmit.value = originalSubmit.value;
    } else {
      stickySubmit.textContent = originalSubmit.textContent;
    }

    stickySubmit.disabled = originalSubmit.disabled;

    stickySubmit.classList.toggle('hide', originalSubmit.classList.contains('hide'));
    stickySubmit.classList.toggle('disabled', originalSubmit.classList.contains('disabled'));
  };

  const handleStickyLabelClick = (event, stickyRoot, stickyForm) => {
    const label = event.target.closest('label[for]');

    if (!label || !stickyRoot.contains(label)) return;

    const input = findInputByIdInside(stickyForm, label.getAttribute('for'));

    if (!input) return;

    event.preventDefault();

    if (input.matches('input[type="radio"]')) {
      if (!input.checked) {
        input.checked = true;
        dispatchChange(input);
      }

      return;
    }

    if (input.matches('input[type="checkbox"]')) {
      input.checked = !input.checked;
      dispatchChange(input);
      return;
    }

    input.focus();
  };

  const handleStickyJewelryService = (event, stickyRoot, stickyForm, originalForm) => {
    const stickyOption = event.target.closest('.npdp-custom-select .drop-option');

    if (!stickyOption || !stickyRoot.contains(stickyOption)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const stickyCustomSelect = stickyOption.closest('.npdp-custom-select');
    const stickySelectedText = stickyCustomSelect?.querySelector('.selected-opt p');

    stickyCustomSelect?.querySelectorAll('.drop-option').forEach((option) => {
      option.classList.toggle('selected', option === stickyOption);
    });

    if (stickySelectedText) {
      const optionName = stickyOption.dataset.name || stickyOption.textContent.trim();
      const optionColor = stickyOption.dataset.color || '';

      if (optionColor) {
        stickySelectedText.innerHTML = `
          <span class="drop-color" style="--bg: ${optionColor}"></span>
          ${optionName}
        `;
      } else {
        stickySelectedText.textContent = optionName;
      }
    }

    stickyCustomSelect?.classList.remove('open');

    const stickyJewelryInputs = stickyForm.querySelectorAll('.jewerly-inputs input');

    if (stickyOption.dataset.id === 'none') {
      stickyJewelryInputs.forEach((input) => {
        input.setAttribute('disabled', 'true');
      });
    } else {
      const stickyIdInput = stickyForm.querySelector('.jewerly-inputs input[name="items[1][id]"]');

      if (stickyIdInput) {
        stickyIdInput.value = stickyOption.dataset.id;
      }

      stickyJewelryInputs.forEach((input) => {
        input.removeAttribute('disabled');
      });
    }

    const originalCustomSelect = originalForm.querySelector('.npdp-custom-select');

    if (originalCustomSelect) {
      const originalOption = Array.from(originalCustomSelect.querySelectorAll('.drop-option')).find((option) => {
        return option.dataset.id === stickyOption.dataset.id;
      });

      if (originalOption) {
        originalOption.click();
      }
    }

    setTimeout(() => {
      syncStickyExtrasToOriginal(stickyForm, originalForm);
    }, 50);
  };

  const submitOriginalFormFromSticky = (event, stickyForm, originalForm) => {
    event.preventDefault();

    const originalSubmit = originalForm.querySelector('input[type="submit"], button[type="submit"]');

    originalSubmit?.click();
  };

  const initStickyControlsBridge = (stickyRoot) => {
    const stickyForm = getStickyForm(stickyRoot);
    const originalForm = getOriginalForm(stickyRoot);

    if (!stickyForm || !originalForm) return;

    stickyRoot.addEventListener(
      'click',
      (event) => {
        handleStickyLabelClick(event, stickyRoot, stickyForm);
        handleStickyJewelryService(event, stickyRoot, stickyForm, originalForm);
      },
      true
    );

    stickyForm.addEventListener('change', (event) => {
      const target = event.target;

      if (
        target.matches('.option:not(.opt-extra) select') ||
        target.matches('.option:not(.opt-extra) input[type="radio"]')
      ) {
        syncStickyOptionToOriginal(target, stickyForm, originalForm);

        setTimeout(() => {
          syncOriginalOptionsToSticky(stickyForm, originalForm);
          syncStickyLeadTime(stickyRoot);
        }, 150);

        return;
      }

      if (
        target.matches('.opt-extra input, .opt-extra select, .opt-extra textarea') ||
        target.matches('.npdp-engraving input, .npdp-engraving select, .npdp-engraving textarea') ||
        target.matches('.final-sale input')
      ) {
        syncStickyExtrasToOriginal(stickyForm, originalForm);

        setTimeout(() => {
          syncStickySubmitButton(stickyForm, originalForm);
        }, 100);
      }
    });

    originalForm.addEventListener('change', () => {
      setTimeout(() => {
        syncOriginalOptionsToSticky(stickyForm, originalForm);
        syncStickyLeadTime(stickyRoot);
      }, 100);
    });

    stickyForm.addEventListener('submit', (event) => {
      submitOriginalFormFromSticky(event, stickyForm, originalForm);
    });

    const originalSubmit = originalForm.querySelector('input[type="submit"], button[type="submit"]');

    if (originalSubmit) {
      const observer = new MutationObserver(() => {
        syncStickySubmitButton(stickyForm, originalForm);
      });

      observer.observe(originalSubmit, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'value']
      });
    }

    setTimeout(() => {
      syncOriginalOptionsToSticky(stickyForm, originalForm);
    }, 300);

    const originalLeadTime = getOriginalLeadTimeElements();

    if (originalLeadTime.ltCon || originalLeadTime.shipTime) {
      const leadTimeObserver = new MutationObserver(() => {
        syncStickyLeadTime(stickyRoot);
      });

      if (originalLeadTime.ltCon) {
        leadTimeObserver.observe(originalLeadTime.ltCon, {
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      }

      if (originalLeadTime.shipTime) {
        leadTimeObserver.observe(originalLeadTime.shipTime, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }
  };

  onReady(() => {
    document.querySelectorAll(STICKY_ROOT_SELECTOR).forEach(initStickyControlsBridge);
  });
})();